import MobileBasicButton from 'components/Buttons/MobileBasicButton';
import { connectMindPaths } from 'constants/path';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import sungnamEnteranceImg from 'assets/SungnamIcons/Img/sungnamEnterance.png';
import { DiagnosisStatus } from 'utils/statusCode';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';
import { GetAssessmentAiResultResponse } from 'models/MindUp/MindUpApiTypes';
import useToggle from 'hooks/CustomHooks/useToggle';
import { StorageKeys } from 'constants/storageKeys';
import MobileLeaveModalVertical from 'components/Modal/MobileLeaveModalVertical';

const MAX_DIAGNOSIS_COUNT = 600; // 예산 소진 기준 카운트

const SungnamEnterancePage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isVerified, setIsVerified] = useState(true);
    const [showPreviousModal, setShowPreviousModal] = useToggle();
    const [showBudgetExhaustedModal, setShowBudgetExhaustedModal] = useToggle();

    const [diagnosisList, setDiagnosisList] = useState<GetAssessmentAiResultResponse[] | null>(null);
    const [token, setBearerToken] = useState('');
    const [loading, setLoading] = useState(true);
    const [deviceKeyCount, setDeviceKeyCount] = useState<number>(0);

    useEffect(() => {
        const pathName = _.split(location.pathname, '/');
        sessionStorage.setItem(StorageKeys.ENCRYPTED_DEVICE_KEY, _.last(pathName) ?? '');
    }, [location.pathname]);

    useEffect(() => {
        const encryptedKey = _.last(_.split(location.pathname, '/')) ?? '';
        let mounted = true;

        setLoading(true);
        
        // 병렬로 두 API 호출
        Promise.all([
            mindUpDiagnosisApi.getListByDeviceKey(encryptedKey),
            mindUpDiagnosisApi.getDeviceKeyCount(),
        ])
            .then(([listRes, countRes]) => {
                if (!mounted) return;
                setIsVerified(true);
                const list = listRes.data.list ?? [];
                setDiagnosisList(list);
                setBearerToken(listRes.data.token ?? '');
                setDeviceKeyCount(countRes.count ?? 0);
                
                // 카운트 체크: API에서 받은 카운트가 특정 카운트를 넘으면 모달 표시
                console.log(countRes.count);
                if ((countRes.count ?? 0) >= MAX_DIAGNOSIS_COUNT) {
                    setShowBudgetExhaustedModal();
                }
            })
            .catch((error) => {
                if (!mounted) return;
                setIsVerified(false);
                navigate(connectMindPaths.sungnamError, {
                    replace: false,
                    state: { errorCode: error.code },
                });
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [location.pathname, navigate, setShowBudgetExhaustedModal]);

    const handleStart = () => {
        if (loading || !token) return;

        if ((Array.isArray(diagnosisList) && diagnosisList.length === 0) || _.first(diagnosisList)?.data.deviceKey === '618fbc4bf14e6879') {
            navigate(connectMindPaths.sungnamAgreement, { replace: false, state: { data: token } });
            return;
        }

        const hasInProgress = _.findLast(diagnosisList, (item) => item.data.status === DiagnosisStatus.inProgress);
        if (hasInProgress) {
            setShowPreviousModal();
            return;
        }
    };

    const handleGoList = () => {
        if (loading) return;
        navigate(connectMindPaths.sungnamDiagnosisHistoryList, {
            replace: false,
            state: { diagnosisList: diagnosisList ?? [], token },
        });
    };

    const isBudgetExhausted = deviceKeyCount >= MAX_DIAGNOSIS_COUNT;

    const verifiedUI = (
        <Layout>
            {showPreviousModal && (
                <MobileLeaveModalVertical
                    setIsLeaveModal={() => {
                        navigate(connectMindPaths.sungnamAgreement, { replace: false, state: { data: token } });
                        setShowPreviousModal();
                    }}
                    title='진행중인 검사가 있어요'
                    content={`서비스를 새로 진행하거나\n검사 목록을 확인해주세요.`}
                    firstButton='서비스 진행하기'
                    firstButtonType='gray'
                    clickLeaveButtonInLeaveModal={() => {
                        handleGoList();
                        setShowPreviousModal();
                    }}
                    secondButton='검사 목록보기'
                    secondButtonType='fill'
                />
            )}

            {showBudgetExhaustedModal && (
                <MobileLeaveModalVertical
                    setIsLeaveModal={setShowBudgetExhaustedModal}
                    title='준비된 예산이 모두 소진되어 이용할수 없습니다.'
                    firstButton='확인'
                    firstButtonType='fill'
                />
            )}

            <ImgWrapper src={sungnamEnteranceImg} alt='sungnamEnteranceImg' />

            <ButtonContainer>
                {/* 로딩 중에는 버튼 아예 렌더하지 않음(= 깜빡임 제거) */}
                {!loading && (
                    <>
                        {Array.isArray(diagnosisList) && diagnosisList.length === 0 ? (
                            <MobileBasicButton onClick={handleStart} $type='fill' $height='52px' disabled={loading || !token || isBudgetExhausted}>
                                서비스 진행하기
                            </MobileBasicButton>
                        ) : (
                            <MobileBasicButton onClick={handleGoList} $type='gray' $height='52px' disabled={loading || isBudgetExhausted}>
                                검사 목록보기
                            </MobileBasicButton>
                        )}
                    </>
                )}
            </ButtonContainer>
        </Layout>
    );

    return isVerified ? verifiedUI : <>잘못된 경로입니다.</>;
};

export default SungnamEnterancePage;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0px 20px;
    gap: 36px;
`;

const ImgWrapper = styled.img`
    max-width: 360px;
    height: 308px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
    justify-content: center;
    align-items: center;
`;
